import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatButton} from '@angular/material/button';
import {MatFormField, MatHint, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatOption, MatSelect} from '@angular/material/select';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatSnackBar} from '@angular/material/snack-bar';
import {SharedModule} from 'src/app/shared/shared.module';
import {AdxInvoiceErpAdjustmentSimulationService} from '../../service/adx-invoice-erp-adjustment-simulation.service';
import {
  ADJUSTMENT_TYPE_REFUND,
  ADJUSTMENT_TYPE_VOUCHER,
  AdjustmentTypeOption,
  BookingOption,
  CurrencyOption,
  InvoiceOption,
  LegalEntityOption,
} from '../../model/adx-invoice-erp-adjustment-simulation';

export interface AddErpAdjustmentDialogData {
  tripId: number;
}

const MAX_LENGTH = 100;

@Component({
  selector: 'app-add-erp-adjustment-dialog',
  imports: [
    MatButton,
    MatFormField,
    MatHint,
    MatInput,
    MatLabel,
    MatOption,
    MatProgressSpinner,
    MatSelect,
    ReactiveFormsModule,
    SharedModule,
  ],
  templateUrl: './add-erp-adjustment-dialog.component.html',
  styles: [`
    .add-adjustment__fields { display: flex; flex-direction: column; gap: 8px; min-width: 460px; }
    mat-form-field { width: 100%; }
    .add-adjustment__line { border: 1px solid rgba(0, 0, 0, 0.2); border-radius: 4px; margin: 0 0 12px; padding: 8px 12px 0; }
    .add-adjustment__line legend { font-size: 13px; padding: 0 4px; }
    .add-adjustment__preview { font-size: 12px; color: rgba(0, 0, 0, 0.6); margin: 0 0 8px; }
  `],
})
export class AddErpAdjustmentDialogComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef = inject(MatDialogRef<AddErpAdjustmentDialogComponent>);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);
  private readonly service = inject(AdxInvoiceErpAdjustmentSimulationService);
  protected readonly data = inject<AddErpAdjustmentDialogData>(MAT_DIALOG_DATA);

  protected readonly maxLength = MAX_LENGTH;
  protected readonly voucherType = ADJUSTMENT_TYPE_VOUCHER;
  protected readonly refundType = ADJUSTMENT_TYPE_REFUND;

  protected adjustmentTypes: AdjustmentTypeOption[] = [];
  protected legalEntities: LegalEntityOption[] = [];
  protected currencies: CurrencyOption[] = [];
  protected invoices: InvoiceOption[] = [];
  protected bookings: BookingOption[] = [];
  protected saving = false;

  protected readonly form = this.fb.group({
    adxInvoiceId: this.fb.control<number | null>(null, Validators.required),
    adxBookingIds: this.fb.control<number[]>([]),
    legalEntityId: this.fb.control<number | null>(null, Validators.required),
    adjustmentTypeId: this.fb.control<number | null>(null, Validators.required),
    pnr: this.fb.control<string>('', Validators.required),
    rawText: this.fb.control<string>('', [Validators.required, Validators.maxLength(MAX_LENGTH)]),
    lines: this.fb.array<FormGroup>([]),
  });

  public ngOnInit(): void {
    this.service.getAdjustmentTypes().pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(types => this.adjustmentTypes = types);
    this.service.getLegalEntities().pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(entities => this.legalEntities = entities);
    this.service.getCurrencies().pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(currencies => {
        this.currencies = currencies;
        this.applyBookingCurrencies();
      });

    this.service.getInvoicesForTrip(this.data.tripId).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(invoices => {
        this.invoices = invoices;
        // Only one ADX invoice can be selected; preselect when the trip has just one.
        if (invoices.length === 1) {
          this.form.controls.adxInvoiceId.setValue(invoices[0].id);
          this.onInvoiceChanged();
        }
      });

    this.form.controls.adjustmentTypeId.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateBookingValidators();
        this.syncLines();
      });
  }

  protected get isRefund(): boolean {
    return this.form.controls.adjustmentTypeId.value === ADJUSTMENT_TYPE_REFUND;
  }

  protected get selectedLegalEntity(): LegalEntityOption | undefined {
    return this.legalEntities.find(x => x.id === this.form.controls.legalEntityId.value);
  }

  /** Preview of the {DataAreaId}-{rawText} value used for invoice/order/trailer/account code. */
  protected get prefixedPreview(): string {
    const entity = this.selectedLegalEntity;
    const raw = this.form.controls.rawText.value ?? '';
    return entity && raw ? `${entity.dataAreaId}-${raw}`.substring(0, MAX_LENGTH) : '';
  }

  /** Voucher invoice numbers carry the MAN segment: {DataAreaId}-MAN-{raw}. */
  protected get invoicePreview(): string {
    const entity = this.selectedLegalEntity;
    const raw = this.form.controls.rawText.value ?? '';
    return entity && raw
      ? `${entity.dataAreaId}-${this.isRefund ? '' : 'MAN-'}${raw}`.substring(0, MAX_LENGTH)
      : '';
  }

  protected onInvoiceChanged(): void {
    // Booking selection is tied to the invoice, so it is always reset and reloaded.
    this.form.controls.adxBookingIds.setValue([]);
    this.bookings = [];
    this.syncLines();

    const invoiceId = this.form.controls.adxInvoiceId.value;
    if (!invoiceId) {
      return;
    }

    const invoice = this.invoices.find(x => x.id === invoiceId);
    this.form.controls.pnr.setValue(invoice?.recordLocator ?? '');

    // Legal entity is mapped on the ADX invoice; when it has none the user picks one from the dropdown.
    const legalEntityId = invoice?.legalEntityId ?? null;
    this.form.controls.legalEntityId.setValue(
      legalEntityId !== null && this.legalEntities.some(x => x.id === legalEntityId) ? legalEntityId : null);

    this.service.getBookingsForInvoice(invoiceId).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(bookings => {
        this.bookings = bookings;
        this.applyBookingCurrencies();
      });
  }

  protected get lines(): FormArray<FormGroup> {
    return this.form.controls.lines;
  }

  protected onBookingsChanged(): void {
    this.syncLines();
  }

  protected bookingLabelById(id: number | null): string {
    const booking = this.bookings.find(x => x.id === id);
    return booking ? this.bookingLabel(booking) : '';
  }

  /**
   * Voucher: one line (no booking). Refund: one line per selected booking, keeping the values
   * already typed for bookings that stay selected. Amounts/currencies/item number live at line level.
   */
  private syncLines(): void {
    const lines = this.lines;

    if (!this.form.controls.adjustmentTypeId.value) {
      lines.clear();
      return;
    }

    if (!this.isRefund) {
      if (lines.length !== 1 || lines.at(0).controls['adxBookingId'].value !== null) {
        lines.clear();
        lines.push(this.createLine(null));
      }
      return;
    }

    const selected = this.form.controls.adxBookingIds.value ?? [];

    for (let i = lines.length - 1; i >= 0; i--) {
      const id = lines.at(i).controls['adxBookingId'].value;
      if (id === null || !selected.includes(id)) {
        lines.removeAt(i);
      }
    }

    for (const id of selected) {
      if (!lines.controls.some(g => g.controls['adxBookingId'].value === id)) {
        lines.push(this.createLine(id));
      }
    }
  }

  private createLine(adxBookingId: number | null): FormGroup {
    const group = this.fb.group({
      adxBookingId: this.fb.control<number | null>(adxBookingId),
      itemNumber: this.fb.control<string>('', adxBookingId === null ? [] : [Validators.required, Validators.maxLength(MAX_LENGTH)]),
      costPrice: this.fb.control<number | null>(null, [Validators.required, Validators.min(0)]),
      costCurrency: this.fb.control<string | null>(null, Validators.required),
      salesPrice: this.fb.control<number | null>(null, [Validators.required, Validators.min(0)]),
      salesCurrency: this.fb.control<string | null>(null, Validators.required),
    });

    this.applyBookingCurrency(group);

    return group;
  }

  /** Refund lines take their currency from the booking (read-only); voucher lines pick it from the dropdown. */
  private applyBookingCurrency(group: FormGroup): void {
    const bookingId = group.controls['adxBookingId'].value;
    const isRefundLine = this.isRefund && bookingId !== null;

    for (const name of ['costCurrency', 'salesCurrency']) {
      const control = group.controls[name];

      if (!isRefundLine) {
        control.enable({ emitEvent: false });
        continue;
      }

      const bookingCurrencyId = this.bookings.find(x => x.id === bookingId)?.currencyId;
      const code = this.currencies.find(x => x.id === bookingCurrencyId)?.code ?? null;
      control.setValue(code, { emitEvent: false });
      control.disable({ emitEvent: false });
    }
  }

  private applyBookingCurrencies(): void {
    this.lines.controls.forEach(group => this.applyBookingCurrency(group));
  }

  protected bookingLabel(booking: BookingOption): string {
    const detail = [booking.carrier, booking.itinerary].filter(Boolean).join(' ');
    const fare = booking.totalFare != null ? ` - ${booking.totalFare.toFixed(2)}` : '';
    return `#${booking.id}${detail ? ' ' + detail : ''}${fare}`;
  }

  protected cancel(): void {
    this.dialogRef.close(false);
  }

  protected submit(): void {
    if (this.form.invalid || this.lines.length === 0 || this.saving) {
      return;
    }

    const v = this.form.getRawValue();
    this.saving = true;

    this.service.simulate({
      adxInvoiceId: v.adxInvoiceId!,
      legalEntityId: v.legalEntityId!,
      adxInvoiceErpAdjustmentTypeId: v.adjustmentTypeId!,
      rawText: v.rawText!.trim(),
      pnr: v.pnr?.trim() || null,
      lines: (v.lines as any[]).map(l => ({
        adxBookingId: this.isRefund ? l.adxBookingId : null,
        itemNumber: this.isRefund ? (l.itemNumber ?? '').trim() : null,
        costPrice: l.costPrice,
        salesPrice: l.salesPrice,
        costCurrency: l.costCurrency,
        salesCurrency: l.salesCurrency,
      })),
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: result => {
        this.saving = false;
        this.snackBar.open(
          result.publishSkipped
            ? 'Adjustment created but not published (no message tracking record for this invoice).'
            : 'Simulated ERP response applied and published.',
          'Close',
          {duration: 6000});
        this.dialogRef.close(true);
      },
      error: error => {
        this.saving = false;
        // Surface field-level validation messages (ValidationProblemDetails.errors) when present.
        const fieldErrors = Object.values(error?.error?.errors ?? {}).flat().join(' ');
        this.snackBar.open(
          fieldErrors || error?.error?.detail || error?.error?.title || error?.message || 'Simulation failed.',
          'Close',
          {duration: 10000});
      },
    });
  }

  private updateBookingValidators(): void {
    const control = this.form.controls.adxBookingIds;
    control.setValidators(this.isRefund ? [Validators.required, Validators.minLength(1)] : []);
    if (!this.isRefund) {
      control.setValue([]);
    }
    control.updateValueAndValidity();
  }
}
